import React from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
} from "@mui/material";
import { MAIN_COLOR_RED } from "../assets/styles";
import { SavedShoppingList } from "../redux/shoppingListSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AcUnitIcon from "@mui/icons-material/AcUnit"; // Snowflake for frozen items
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag"; // Heavy items
import StarBorderIcon from "@mui/icons-material/StarBorder"; // Recommendations

export interface ShoppingPreferences {
    frozenItems: boolean;
    heavyItems: boolean;
    recommendations: boolean;
}

interface SelectPreferencesStepProps {
    preferences: ShoppingPreferences;
    selectedList: SavedShoppingList | null;
    onTogglePreference: (key: keyof ShoppingPreferences) => void;
    onBack: () => void;
    onContinue: () => void;
    /** When true, hide the Back/Shop button row (e.g. when used inside a dialog) */
    hideActions?: boolean;
}

const SelectPreferencesStep: React.FC<SelectPreferencesStepProps> = ({
    preferences,
    selectedList,
    onTogglePreference,
    onBack,
    onContinue,
    hideActions = false,
}) => {
    return (
        <Box>
            <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: MAIN_COLOR_RED, mb: 1 }}
            >
                Shopping Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select what to prioritize when optimizing your route.
            </Typography>

            <Grid container spacing={2}>
                {/* Frozen Items */}
                <Grid item xs={12}>
                    <Card
                        sx={{
                            borderRadius: 2,
                            boxShadow: 2,
                            cursor: "pointer",
                            border: preferences.frozenItems ? `2px solid ${MAIN_COLOR_RED}` : "2px solid transparent",
                            "&:hover": {
                                boxShadow: 4,
                            },
                        }}
                        onClick={() => onTogglePreference("frozenItems")}
                    >
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                <AcUnitIcon
                                    sx={{
                                        fontSize: 48,
                                        color: MAIN_COLOR_RED,
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        Frozen items
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Collect your frozen items at the end of your route.
                                    </Typography>
                                </Box>
                                {preferences.frozenItems ? (
                                    <CheckCircleIcon sx={{ color: MAIN_COLOR_RED, fontSize: 32 }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ color: "#e0e0e0", fontSize: 32 }} />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Heavy Items */}
                <Grid item xs={12}>
                    <Card
                        sx={{
                            borderRadius: 2,
                            boxShadow: 2,
                            cursor: "pointer",
                            border: preferences.heavyItems ? `2px solid ${MAIN_COLOR_RED}` : "2px solid transparent",
                            "&:hover": {
                                boxShadow: 4,
                            },
                        }}
                        onClick={() => onTogglePreference("heavyItems")}
                    >
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                <ShoppingBagIcon
                                    sx={{
                                        fontSize: 48,
                                        color: MAIN_COLOR_RED,
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        Heavy items
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Collect your heavy items at the beginning of your route.
                                    </Typography>
                                </Box>
                                {preferences.heavyItems ? (
                                    <CheckCircleIcon sx={{ color: MAIN_COLOR_RED, fontSize: 32 }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ color: "#e0e0e0", fontSize: 32 }} />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recommendations */}
                <Grid item xs={12}>
                    <Card
                        sx={{
                            borderRadius: 2,
                            boxShadow: 2,
                            cursor: "pointer",
                            border: preferences.recommendations ? `2px solid ${MAIN_COLOR_RED}` : "2px solid transparent",
                            "&:hover": {
                                boxShadow: 4,
                            },
                        }}
                        onClick={() => onTogglePreference("recommendations")}
                    >
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                <StarBorderIcon
                                    sx={{
                                        fontSize: 48,
                                        color: MAIN_COLOR_RED,
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        Recommendations
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Check out product recommendations near your route.
                                    </Typography>
                                </Box>
                                {preferences.recommendations ? (
                                    <CheckCircleIcon sx={{ color: MAIN_COLOR_RED, fontSize: 32 }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ color: "#e0e0e0", fontSize: 32 }} />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {!hideActions && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    sx={{
                        borderColor: "#e0e0e0",
                        color: "text.secondary",
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        "&:hover": {
                            borderColor: "#bdbdbd",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                    }}
                >
                    Back
                </Button>
                <Button
                    variant="outlined"
                    onClick={onContinue}
                    disabled={!selectedList}
                    sx={{
                        borderColor: MAIN_COLOR_RED,
                        color: MAIN_COLOR_RED,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        "&:hover": {
                            borderColor: MAIN_COLOR_RED,
                            backgroundColor: "rgba(220, 38, 38, 0.04)",
                        },
                        "&:disabled": {
                            borderColor: "#e0e0e0",
                            color: "#9e9e9e",
                        },
                    }}
                >
                    Shop
                </Button>
            </Box>
            )}
        </Box>
    );
};

export default SelectPreferencesStep;

